import * as React from 'react';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  CircularProgress,
  LabelDisplayedRowsArgs,
  Table as MuiTable,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableFooter,
  TablePagination,
  TablePaginationProps as MuiTablePaginationProps,
  TableProps as MuiTableProps,
  TableRow,
  tableRowClasses,
} from '@mui/material';

import readJsonFromLocalStorage from '@/utils/readJsonFromLocalStorage';
import writeJsonToLocalStorage from '@/utils/writeJsonToLocalStorage';

const Styles = {
  Table: {
    [`& .${tableCellClasses.head}`]: {
      typography: 'subtitle1',
    },
    [`& .${tableCellClasses.body}`]: {
      typography: 'body1',
    },
  },
  TableColored: {
    [`& .${tableRowClasses.root}:nth-of-type(even)`]: {
      bgcolor: 'grey.50',
    },
  },
  TablePagination: {
    borderBottom: 0,
  },
} as const;

interface PlootoTableProps {
  totalNumberOfRecords?: number;
  isLoading?: boolean;
  isError?: boolean;
  isColored?: boolean;
  showPagination?: boolean;
  pageSizeOptions?: Array<number>;
  pageSize?: number;
  currentPageIndex?: number;
  labelRowsPerPage?: MuiTablePaginationProps['labelRowsPerPage'];
  errorComponent?: ReactNode;
  noRowsComponent?: ReactNode;
  labelDisplayedRows?: MuiTablePaginationProps['labelDisplayedRows'];
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  localStorageKey?: string;
}

type TableProps = PlootoTableProps & MuiTableProps;

const defaultPageSize = 10;

const DefaultLabelRowsPerPage = <FormattedMessage defaultMessage="Rows per page:" />;
const DefaultLabelDisplayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) => (
  <FormattedMessage defaultMessage="{from}-{to} of {count}" values={{ from, to, count }} />
);
const DefaultErrorComponent = <FormattedMessage defaultMessage="Error loading data" />;
const DefaultNowRowsComponent = <FormattedMessage defaultMessage="No rows matching criteria" />;
/**
 * Plooto Table
 * @param props Table props
 * @returns Function component
 */
const Table: React.FC<TableProps> = ({
  sx,
  children: childrenElement,
  totalNumberOfRecords = 0,
  isLoading = false,
  isError = false,
  isColored = false,
  showPagination = true,
  pageSizeOptions = [10, 20, 50, 100],
  pageSize = defaultPageSize,
  currentPageIndex = 0,
  labelRowsPerPage = DefaultLabelRowsPerPage,
  labelDisplayedRows = DefaultLabelDisplayedRows,
  errorComponent = DefaultErrorComponent,
  noRowsComponent = DefaultNowRowsComponent,
  onPageChange,
  onPageSizeChange,
  localStorageKey,
  size = 'small',
  ...tableProps
}: TableProps) => {
  const children = useMemo(() => React.Children.toArray(childrenElement), [childrenElement]);
  const colgroupShift = useMemo(() => (children.length === 3 ? 1 : 0), [children]);

  const header = children[0 + colgroupShift];
  const rows = children[1 + colgroupShift];

  const handlePageChange = (_, pageNumber: number) => {
    if (onPageChange) {
      onPageChange(pageNumber);
    }
  };

  const handlePageSizeChange = (event) => {
    if (onPageSizeChange) {
      const newPageSize = Number(event.target.value);
      onPageSizeChange(newPageSize);
    }
  };

  const onPageSizeChangeCallback = useCallback(
    (newPageSize: number) => onPageSizeChange?.(newPageSize),
    [onPageSizeChange]
  );

  useEffect(() => {
    if (!localStorageKey) {
      return;
    }
    const savedPageSize = readJsonFromLocalStorage<number>(localStorageKey, 10);

    if (savedPageSize !== defaultPageSize) {
      onPageSizeChangeCallback?.(savedPageSize);
    }
  }, [localStorageKey, onPageSizeChangeCallback]);

  useEffect(() => {
    if (!localStorageKey) {
      return;
    }
    writeJsonToLocalStorage(localStorageKey, pageSize);
  }, [localStorageKey, pageSize]);

  const totalPages = Math.ceil(totalNumberOfRecords / pageSize);
  useEffect(() => {
    if (totalPages > 0 && currentPageIndex >= totalPages) {
      onPageChange?.(Math.max(0, totalPages - 1));
    }
  }, [currentPageIndex, onPageChange, totalPages]);

  const pagination = (
    <TableFooter>
      <TableRow>
        <TablePagination
          sx={Styles.TablePagination}
          SelectProps={{
            disabled: isLoading,
            MenuProps: {
              disableScrollLock: true,
            },
          }}
          backIconButtonProps={isLoading ? { disabled: isLoading } : undefined}
          nextIconButtonProps={isLoading ? { disabled: isLoading } : undefined}
          rowsPerPageOptions={pageSizeOptions}
          count={totalNumberOfRecords}
          rowsPerPage={pageSize}
          page={currentPageIndex}
          labelRowsPerPage={labelRowsPerPage}
          labelDisplayedRows={labelDisplayedRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </TableRow>
    </TableFooter>
  );

  const getTableBody = () => {
    if (isLoading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={1000}>
              <Box height={320} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    if (isError) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={1000}>
              <Box height={320} display="flex" alignItems="center" justifyContent="center">
                {errorComponent}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    if (totalNumberOfRecords === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={1000}>
              <Box height={320} display="flex" alignItems="center" justifyContent="center">
                {noRowsComponent}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return rows;
  };

  return (
    <TableContainer>
      <MuiTable
        {...tableProps}
        sx={[
          Styles.Table,
          isColored ? Styles.TableColored : undefined,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        size={size}
      >
        {header}
        {getTableBody()}
        {showPagination ? pagination : null}
      </MuiTable>
    </TableContainer>
  );
};

export type { TableProps };
export default Table;
